import { useForm } from '@tanstack/react-form'
import { presupuestoSchema, type PresupuestoFormValues } from './presupuestoSchema'
import { useConfiguracionEstudio } from '@/hooks/useConfiguracion'
import type { Presupuesto } from '@/types/database'
import { parsePresupuestoItems, detectarTipoPersona } from '../presupuesto-utils'
import { hoyEnArgentina } from '@/core/services/tareasRecurrentesService'

interface UsePresupuestoFormOptions {
  presupuesto?: Presupuesto | null
  onSubmit?: (values: PresupuestoFormValues) => void | Promise<void>
}

export function usePresupuestoForm(options: UsePresupuestoFormOptions = {}) {
  const { presupuesto, onSubmit } = options
  const { data: configEstudio } = useConfiguracionEstudio()

  const defaultValidezDias = configEstudio?.validez_dias_default ?? 15
  const hoy = hoyEnArgentina()

  // Map presupuesto items to form items format
  const mapPresupuestoItemsToFormItems = (items: ReturnType<typeof parsePresupuestoItems>) => {
    return items.map((item) => ({
      servicio_id: item.servicio_id ?? '',
      cantidad: item.cantidad ?? 1,
      precio_unitario: item.precio_unitario ?? 0,
      descuento_porcentaje: item.descuento_porcentaje ?? 0,
      subtotal: item.subtotal ?? 0,
      descripcion: item.descripcion ?? null,
    }))
  }

  // Calculate default values from presupuesto (edit mode) or empty (create mode)
  const defaultValues: PresupuestoFormValues = presupuesto
    ? {
        modo_cliente: presupuesto.cliente_id ? 'existente' : 'nuevo',
        cliente_id: presupuesto.cliente_id,
        cliente_nombre: presupuesto.cliente_nombre ?? '',
        cliente_cuit: presupuesto.cliente_cuit,
        cliente_email: presupuesto.cliente_email,
        cliente_condicion_iva: null,
        tipo_persona: detectarTipoPersona(presupuesto.cliente_cuit),
        admin_nombre: presupuesto.admin_nombre ?? null,
        admin_cuit: presupuesto.admin_cuit ?? null,
        fecha: presupuesto.fecha ?? hoy,
        validez_dias: presupuesto.validez_dias ?? defaultValidezDias,
        condiciones_pago: presupuesto.condiciones_pago,
        items:
          parsePresupuestoItems(presupuesto).length > 0
            ? mapPresupuestoItemsToFormItems(parsePresupuestoItems(presupuesto))
            : [{ servicio_id: '', cantidad: 1, precio_unitario: 0, descuento_porcentaje: 0, subtotal: 0, descripcion: null }],
        descuento_porcentaje: presupuesto.descuento_porcentaje ?? 0,
        iva_porcentaje: (presupuesto.iva_porcentaje ?? 21).toString(),
        notas: presupuesto.notas,
      }
    : {
        modo_cliente: 'existente',
        cliente_id: null,
        cliente_nombre: '',
        cliente_cuit: null,
        cliente_email: null,
        cliente_condicion_iva: null,
        tipo_persona: 'fisica',
        admin_nombre: null,
        admin_cuit: null,
        fecha: hoy,
        validez_dias: defaultValidezDias,
        condiciones_pago: null,
        items: [{ servicio_id: '', cantidad: 1, precio_unitario: 0, descuento_porcentaje: 0, subtotal: 0, descripcion: null }],
        descuento_porcentaje: 0,
        iva_porcentaje: '21',
        notas: null,
      }

  const form = useForm({
    defaultValues,
    validators: {
      onSubmit: presupuestoSchema,
    },
    onSubmit: async ({ value }) => {
      if (onSubmit) {
        await onSubmit(value)
      }
    },
  })

  return { form }
}

export type UsePresupuestoFormReturn = ReturnType<typeof usePresupuestoForm>
export type PresupuestoForm = UsePresupuestoFormReturn['form']
